import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';

import useNotification from '../../../../core/hooks/use_notification';
import { createPriceListNotificationData, getPriceListNotificationData, updatePriceListNotificationData } from '../../../../core/utils/notification_utils';
import { SubmitButton } from '../../../common/button';
import { GET_ROUTE_PRICE_LISTS_EDIT, ROUTE_PRICE_LISTS } from '../../../../core/constants';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import TextFieldFormik from '../../../common/text_field';
import PageHeader from '../../../common/page_header';
import SectionPaper from '../../../common/section_paper';
import BottomActionBar from '../../../common/bottom_action_bar';
import { getPriceListAction, getPriceListState } from '../../../../features/price/get_price_list';
import { createPriceListAction, createPriceListState } from '../../../../features/price/create_price_list';
import { updatePriceListAction, updatePriceListState } from '../../../../features/price/update_price_list';

const PriceListForm = () => {
    const { showErrorMessage, showNotification } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { priceListId } = useParams();

    const getPriceList = useSelector(getPriceListState);
    const createPriceList = useSelector(createPriceListState);
    const updatePriceList = useSelector(updatePriceListState);

    const initialValues = {
        name: (priceListId && getPriceList?.data?.name) || ''
    };

    const schema = Yup.object().shape({
        name: Yup.string().required()
    });

    useEffect(() => {
        priceListId && dispatch(getPriceListAction(priceListId))
            .then(response => showErrorMessage((getPriceListNotificationData(response))));
    }, [priceListId, dispatch, showErrorMessage]);

    const onSubmit = (values) => {
        const form = { ...values };

        if (!priceListId) {
            dispatch(createPriceListAction(form))
                .then(response => {
                    showNotification(createPriceListNotificationData(response));
                    response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && navigate(GET_ROUTE_PRICE_LISTS_EDIT(response?.payload?.id));
                });
        } else {
            dispatch(updatePriceListAction({ form, id: priceListId }))
                .then(response => {
                    showNotification(updatePriceListNotificationData(response));
                });
        }
    };

    const renderView = () => {
        if (getPriceList?.loading) {
            return (
                <Box mt={ 6 }>
                    <LinearProgress/>
                </Box>
            );
        }

        return (
            <Formik
                initialValues={ initialValues }
                validationSchema={ schema }
                validateOnChange={ false }
                validateOnBlur={ false }
                onSubmit={ onSubmit }>
                { () => (
                    <Box component={ Form }
                         sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                        <Box sx={ { flexGrow: 1, mb: 4 } }>
                            <Stack spacing={ 3 }>
                                <SectionPaper title="Dane Podstawowe">
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="name" label="Nazwa" required/>
                                        </Grid>
                                    </Grid>
                                </SectionPaper>
                            </Stack>
                        </Box>

                        <BottomActionBar>
                            <SubmitButton isLoading={ priceListId ? updatePriceList?.loading : createPriceList?.loading }>
                                Zapisz
                            </SubmitButton>
                        </BottomActionBar>

                    </Box>
                ) }
            </Formik>
        );
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
            <PageHeader
                title={ priceListId ? 'Edycja cennika' : 'Nowy cennik' }
                showBack={ true }
                onBack={ () => navigate(ROUTE_PRICE_LISTS) }
                titleAdornment={
                    priceListId && getPriceList?.data && (
                        <Typography variant="h6" color="textSecondary">
                            ID: { priceListId }
                        </Typography>
                    )
                }
            />
            { renderView() }
        </Box>
    );
};

export default PriceListForm;